<template>
  <div>
    <beast-header/>
    <div class="container">
      <beast-search-box
        v-on:searchEnded="updateSearch"/>
      <div class="row">
        <div class="col-12 beast-package-header">
          <h1 v-text="pkg.name"></h1>
          <p v-text="pkg.description"></p>
        </div>
      </div>
      <div class="row">
        <div class="col-9 beast-package-info">
          <router-view :pkg="pkg" :key="name" :versionName="versionName"/>
        </div>
        <div class="col-3 beast-package-sidebar">
          <ul class="beast-sidebar-links">
            <li class="beast-sidebar-header">
              Package Links
            </li>
            <router-link tag="li" :to="webURL"
                         exact-active-class="beast-sidebar-active">
              <a>Description</a>
            </router-link>
            <router-link tag="li" :to="`${webURL}/contributors`"
                         exact-active-class="beast-sidebar-active">
              <a>Contributors</a>
            </router-link>
            <router-link tag="li" :to="`${webURL}/dependencies`"
                         exact-active-class="beast-sidebar-active">
              <a>Dependencies</a>
            </router-link>
            <router-link tag="li" :to="`${webURL}/versions`"
                         exact-active-class="beast-sidebar-active">
              <a>Versions</a>
            </router-link>
            <router-link v-if="shouldEdit" tag="li" :to="`${webURL}/edit`"
                         exact-active-class="beast-sidebar-active">
              <a>Edit</a>
            </router-link>
          </ul>
          <ul class="beast-sidebar-otherinfo">
            <li class="beast-sidebar-header">
              Package Information
            </li>
            <li class="beast-sidebar-info" v-if="pkg.organization">
              <strong>Organization</strong>
              <router-link :to="`/organization/${pkg.organization.slug}`">
                <span v-text="pkg.organization.slug"></span>
              </router-link>
            </li>
            <li class="beast-sidebar-info">
              <strong>Published</strong>
              <span :title="pkg.version._publishDate.format('DD/MM/YYYY @ HH:mm:ss')"
                    v-text="pkg.version.publishDate"></span>
            </li>
            <li class="beast-sidebar-info" v-show="pkg.version.license">
              <strong>License</strong>
              <span v-text="pkg.version.license"></span>
            </li>
            <li class="beast-sidebar-info" v-show="pkg.version.homepage">
              <strong>Homepage</strong>
              <a :href="pkg.version.homepage">
                <span v-text="pkg.version.homepage"></span>
              </a>
            </li>
            <li class="beast-sidebar-info" v-show="pkg.version.repository">
              <strong>Repository</strong>
              <a :href="pkg.version.repository">
                <span v-text="pkg.version.repository"></span>
              </a>
            </li>
            <li class="beast-sidebar-info" v-show="pkg.keywords">
              <strong>Keywords</strong>
              <span class="beast-sidebar-keyword"
                    v-for="keyword in pkg.keywords"
                    v-bind:key="keyword">
                  <a :href="`/search?query=keywords:${encodeURIComponent(keyword)}`">{{keyword}}</a>
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <beast-footer/>
  </div>
</template>

<style scoped lang="scss">
  .beast-package-header {

    p {
      font-style: italic;
    }
  }

  .beast-package-sidebar {
    ul {
      list-style: none;
      padding: 0;
      margin: 0;
      font-size: 11pt;

      li {
        margin-bottom: 5px;
        padding: 3px 5px;
      }
    }

    .beast-sidebar-links {
      li {
        a {
          color: $beast-dark-gray;
          text-decoration: none;
        }

        &:hover, &.beast-sidebar-active {
          a {
            color: $beast-green;
          }
        }
      }
    }

    .beast-sidebar-header {
      font-weight: bold;
      margin-bottom: 2px;
      background: none;
      color: $beast-dark-gray;
      font-size: 14pt;
    }

    .beast-sidebar-otherinfo {
      margin-top: 5px;
      padding-top: 5px;
      border-top: 1px solid #dcdcdc;

      .beast-sidebar-info {
        strong {
          display: block;
        }
      }

      .beast-sidebar-keyword {
        &:after {
          content: ", ";
        }

        &:last-child:after {
          content: "";
        }
      }
    }

  }
</style>

<script>
import moment from "moment";
import BeastHeader from "../../components/header/BeastHeader.vue";
import BeastFooter from "../../components/footer/BeastFooter.vue";
import BeastSearchBox from "../../components/packages/BeastSearchBox.vue";

export default {
  components: { BeastSearchBox, BeastFooter, BeastHeader },
  props: {
    name: String,
    versionName: String,
    organizationSlug: String
  },
  computed: {
    pkgName() {
      return !this.organizationSlug
        ? this.name
        : `@${this.organizationSlug}/${this.name}`;
    },
    url() {
      return !this.versionName
        ? `/packages/${this.pkgName}`
        : `/packages/${this.pkgName}/${this.versionName}`;
    },
    webURL() {
      return !this.versionName
        ? `/package/${this.pkgName}`
        : `/package/${this.pkgName}/${this.versionName}`;
    },
    shouldEdit() {
      return this.isContributor && !this.versionName;
    }
  },
  data() {
    return {
      me: {
        username: ""
      },
      pkg: {
        id: "",
        name: "",
        description: "",
        owner: {
          username: "",
          fullname: "",
          gravatarHash: ""
        },
        contributors: [],
        public: true,
        version: {
          versionName: "",
          publishDate: moment().toNow(),
          _publishDate: moment(),
          publisher: {
            username: "",
            fullname: "",
            gravatarHash: ""
          },
          license: "",
          homepage: "",
          readme: "",
          repository: "",
        },
        keywords: []
      },
      isContributor: false
    };
  },
  watch: {
    async name() {
      await this.readData();
    }
  },
  methods: {
    displayPackage(pkg) {
      this.pkg = pkg;
    },
    updateSearch(packageName) {
      this.$router.push({
        name: "search",
        query: {
          name: packageName
        }
      });
    },
    async readData() {
      const response = await this.axios.get(this.url);
      this.pkg = response.data;
      this.pkg.version._publishDate = moment(this.pkg.version.publishDate);
      this.pkg.version.publishDate = moment(this.pkg.version._publishDate).fromNow();

      if (this.me !== undefined && this.me !== null) {
        this.pkg.contributors.forEach((contributor) => {
          if (contributor.username === this.me.username) {
            this.isContributor = true;
          }
        });
      }
      this.setTitle(`Package: ${this.pkg.name}`);
    }
  },

  async beforeMount() {
    this.me = JSON.parse(sessionStorage.getItem("user"));
    await this.readData();
  }
};
</script>
